import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  AccountDetailType,
  SellerAccountDetailItem,
  SellerAccountDetailSummary,
  SellerSettlement,
} from '@shared/api/needAccount/needAccount.interface';
import { sellerAccountDetailExportUrl } from '@shared/api/needAccount/sellerAccount.hook';
import {
  accountShopOrderList,
  cancelAccountShopOrderList,
  cancelRebateShopOrderList,
  rebateShopOrderList,
} from '@shared/api/shopOrder/shopOrder.controller';
import Modal from '@shared/components/Modal';
import { api_ } from '@shared/plugIn/axios';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { formatDate } from '@shared/utils/formatDate';
import { Checkbox, DatePicker, Input, Table, TableColumnsType, message } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import { CSVLink } from 'react-csv';

import {
  buildBrandFilterParams,
  sellerAccountUserIdAtom,
  sellerBrandOptionsAtom,
  sellerSelectedBrandIdsAtom,
} from '../SellerAccountContent/accountContent.atom';

interface Props {
  openId: string;
  year: number;
  month: number;
  type?: AccountDetailType;
}

type AccountData = {
  id: string;
  index: number;
  requestedAt: string;
  coffinOutDate: string | null;
  address: string;
  funeralHomeInfo: string;
  senderPhrase: string;
  itemName: string;
  priceRetail: number;
  priceWhole: number;
  priceRebateAgency: number;
  priceRebateWorker: number;
  pricePG: number;
  teamName: string;
  workerName: string;
  workerPhoneNumber: string;
  isAccounted: boolean;
  accountedDate: string | null;
  isRebated: boolean;
  rebatedDate: string | null;
};

const columns: TableColumnsType<AccountData> = [
  {
    title: <p className="break-keep">순번</p>,
    dataIndex: 'index',
    key: 'index',
    align: 'center',
  },
  {
    title: <p className="break-keep">결제일</p>,
    dataIndex: 'requestedAt',
    key: 'requestedAt',
    align: 'center',
    render: (requestedAt: string) => {
      return (
        <p>
          {requestedAt
            ? formatDate(new Date(requestedAt), {
                dateSeparater: '/',
                contains: { year: false },
              })
            : '-'}
        </p>
      );
    },
  },
  {
    title: <p className="break-keep">발인일</p>,
    dataIndex: 'coffinOutDate',
    key: 'coffinOutDate',
    align: 'center',
    render: (coffinOutDate: string | null) => {
      return (
        <p>
          {coffinOutDate
            ? formatDate(new Date(coffinOutDate), {
                dateSeparater: '/',
                contains: { year: false },
              })
            : '-'}
        </p>
      );
    },
  },
  {
    title: '주소',
    dataIndex: 'address',
    key: 'address',
    align: 'center',
    render: (address: string) => {
      return <p className="break-keep">{address?.split('(')[0]}</p>;
    },
  },
  {
    title: '장례식장',
    dataIndex: 'funeralHomeInfo',
    key: 'funeralHomeInfo',
    align: 'center',
    render: (funeralHomeInfo: string) => {
      return <p className="break-keep">{funeralHomeInfo}</p>;
    },
  },
  {
    title: '문구',
    dataIndex: 'senderPhrase',
    key: 'senderPhrase',
    align: 'center',
    render: (senderPhrase: string) => {
      return <p className="break-keep">{senderPhrase}</p>;
    },
  },
  {
    title: '상품명',
    dataIndex: 'itemName',
    key: 'itemName',
    align: 'center',
    render: (itemName: string) => {
      return <p className="whitespace-pre">{itemName}</p>;
    },
  },
  {
    title: '소비자가',
    dataIndex: 'priceRetail',
    key: 'priceRetail',
    align: 'center',
    render: (priceRetail: number) => {
      return <p className="break-keep">{(priceRetail ?? 0).toLocaleString()}원</p>;
    },
  },
  {
    title: '화환',
    dataIndex: 'priceWhole',
    key: 'priceWhole',
    align: 'center',
    render: (priceWhole: number) => {
      return <p className="break-keep">{(priceWhole ?? 0).toLocaleString()}원</p>;
    },
  },
  {
    title: '상조',
    dataIndex: 'priceRebateAgency',
    key: 'priceRebateAgency',
    align: 'center',
    render: (priceRebateAgency: number) => {
      return <p className="break-keep">{(priceRebateAgency ?? 0).toLocaleString()}원</p>;
    },
  },
  {
    title: <p className="break-keep">지도사</p>,
    dataIndex: 'priceRebateWorker',
    key: 'priceRebateWorker',
    align: 'center',
    render: (priceRebateWorker: number) => {
      return <p className="break-keep">{(priceRebateWorker ?? 0).toLocaleString()}원</p>;
    },
  },
  {
    title: 'PG',
    dataIndex: 'pricePG',
    key: 'pricePG',
    align: 'center',
    render: (pricePG: number) => {
      return <p className="break-keep">{(pricePG ?? 0).toLocaleString()}원</p>;
    },
  },
  {
    title: <p className="break-keep">소속</p>,
    dataIndex: 'teamName',
    key: 'teamName',
    align: 'center',
    render: (teamName: string) => {
      return <p className="break-keep">{teamName}</p>;
    },
  },
  {
    title: <p className="break-keep">지도사</p>,
    dataIndex: 'workerName',
    key: 'workerName',
    align: 'center',
    render: (workerName: string) => {
      return <p>{workerName}</p>;
    },
  },
  {
    title: <p className="break-keep">휴대번호</p>,
    dataIndex: 'workerPhoneNumber',
    key: 'workerPhoneNumber',
    align: 'center',
    render: (workerPhoneNumber: string) => {
      return <p>{workerPhoneNumber}</p>;
    },
  },
  {
    title: <p className="break-keep">정산</p>,
    key: 'isAccounted',
    align: 'center',
    render: (_, record) => {
      return (
        <p className="whitespace-pre">
          {record.isAccounted && record.accountedDate
            ? formatDate(new Date(record.accountedDate), {
                contains: { year: false },
              })
            : 'X'}
        </p>
      );
    },
  },
  {
    title: <p className="break-keep">상조</p>,
    key: 'isRebated',
    align: 'center',
    render: (_, record) => {
      return (
        <p className="whitespace-pre">
          {record.isRebated && record.rebatedDate
            ? formatDate(new Date(record.rebatedDate), {
                contains: { year: false },
              })
            : 'X'}
        </p>
      );
    },
  },
];

// ─── 하단 PG수수료 + 정산금 요약 ───
const SettlementFooter: FC<{
  pgFeeTotal: number;
  settlement: SellerSettlement | null;
  isAdmin: boolean;
  totalPriceRetail: number;
}> = function SettlementFooter({ pgFeeTotal, settlement, isAdmin, totalPriceRetail }) {
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  // 바깥 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="flex items-center justify-end gap-3">
      {/* PG수수료 합산 (항상 표시) */}
      <div className="center-box w-fit gap-2 rounded-md p-3 text-sm font-bold theme-text-4 theme-bg-main">
        PG수수료 : {pgFeeTotal.toLocaleString()}원
      </div>

      {/* 정산금 (settlement 있을 때만) */}
      {settlement && (
        <div className="relative" ref={popRef}>
          <button
            type="button"
            className="rounded-md bg-blue-600 px-4 py-3 text-sm font-bold text-white hover:bg-blue-700"
            onClick={() => setOpen((prev) => !prev)}
          >
            정산금 : {settlement.settlementAmount.toLocaleString()}원
          </button>

          {open && (
            <div className="absolute bottom-full right-0 z-50 mb-2 w-64 rounded-md border border-gray-200 bg-white p-3 shadow-lg">
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">화환정산금</span>
                  <span>{settlement.totalPriceWhole.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">상조정산금(마리)</span>
                  <span>+{settlement.totalPriceRebate.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">기타 상조 1/2</span>
                  <span>+{settlement.totalEtcRebateHalf.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">PG수수료 1/2 공제</span>
                  <span className="text-red-600">
                    -{settlement.totalPgFeeHalf.toLocaleString()}원
                  </span>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-1 text-sm font-bold">
                  <span>최종 정산금</span>
                  <span className="text-blue-700">
                    {settlement.settlementAmount.toLocaleString()}원
                  </span>
                </div>
                {isAdmin && (
                  <div className="flex justify-between pt-0.5 text-[10px] text-gray-400">
                    <span>(소비자가-PG) - 정산금</span>
                    <span>
                      {(
                        totalPriceRetail -
                        pgFeeTotal -
                        settlement.settlementAmount
                      ).toLocaleString()}
                      원
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SellerAccountDetailModal: FC<Props> = function SellerAccountDetailModal({
  openId,
  year,
  month,
  type = 'need-account',
}: Props) {
  const isOpen = useRecoilValue(simpleOpenAtomFamily(openId));
  const { sellerRole, isAdmin } = useAuth();
  const role = sellerRole();

  // 선택된 bugoBrand → API 파라미터 변환
  const selectedBrandIds = useRecoilValue(sellerSelectedBrandIdsAtom);
  const brandOptions = useRecoilValue(sellerBrandOptionsAtom);
  const userId = useRecoilValue(sellerAccountUserIdAtom);
  const brandFilter = useMemo(
    () => buildBrandFilterParams(selectedBrandIds, brandOptions),
    [selectedBrandIds, brandOptions],
  );

  const [detailData, setDetailData] = useState<SellerAccountDetailItem[]>([]);
  const [settlement, setSettlement] = useState<SellerSettlement | null>(null);
  const [summary, setSummary] = useState<SellerAccountDetailSummary | null>(null);
  const [loading, setLoading] = useState(false);

  // ─── 검색 ───
  const [searchText, setSearchText] = useState('');

  // ─── Admin 전용: 체크박스 선택 + 정산일/상조정산일 변경 ───
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState(false);
  const [accountDate, setAccountDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [rebateDate, setRebateDate] = useState<dayjs.Dayjs | null>(dayjs());

  const fetchDetailData = useCallback(async () => {
    setLoading(true);
    try {
      const url = sellerAccountDetailExportUrl(
        role,
        year,
        month,
        type,
        brandFilter,
        userId,
      );
      const { data } = await api_.get(url);
      // 응답이 { data: [], pagination: {}, settlement?: {}, summary?: {} } 형태일 수 있음
      const items = Array.isArray(data) ? data : data.data ?? [];
      setDetailData(items);
      setSettlement(data.settlement ?? null);
      setSummary(data.summary ?? null);
    } catch (err) {
      console.error('세부내역 불러오기 실패', err);
      message.error('세부내역을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [role, year, month, type, brandFilter, userId]);

  useEffect(() => {
    if (isOpen) {
      fetchDetailData();
    }
  }, [isOpen, fetchDetailData]);

  const tableData: AccountData[] = useMemo(() => {
    return detailData.map((item, index) => ({
      id: item._id ?? `detail-${index}`,
      index: detailData.length - index,
      requestedAt: item.requestedAt,
      coffinOutDate: item.coffinOutDate ?? null,
      address: item.funeralHomeAddress ?? '',
      funeralHomeInfo: item.funeralHomeName ?? '',
      senderPhrase: item.senderPhrase ?? '',
      itemName: item.shopItemName ?? '',
      priceRetail: item.priceRetail ?? 0,
      priceWhole: item.priceWhole ?? 0,
      priceRebateAgency: item.priceRebateAgency ?? 0,
      priceRebateWorker: item.priceRebateWorker ?? 0,
      pricePG: item.pricePG ?? 0,
      teamName: item.teamName ?? '',
      workerName: item.workerName ?? '',
      workerPhoneNumber: item.workerPhoneNumber ?? '',
      isAccounted: item.isAccounted ?? false,
      accountedDate: item.accountedDate ?? null,
      isRebated: item.isRebated ?? false,
      rebatedDate: item.rebatedDate ?? null,
    }));
  }, [detailData]);

  // 검색 필터 적용
  const filteredTableData = useMemo(() => {
    if (!searchText.trim()) return tableData;
    const keyword = searchText.trim().toLowerCase();
    return tableData.filter(
      (row) =>
        row.funeralHomeInfo.toLowerCase().includes(keyword) ||
        row.senderPhrase.toLowerCase().includes(keyword) ||
        row.workerName.toLowerCase().includes(keyword) ||
        row.teamName.toLowerCase().includes(keyword),
    );
  }, [tableData, searchText]);

  // 모달 열릴 때 선택/검색 초기화
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setSearchText('');
    }
  }, [isOpen]);

  // admin 전용 체크박스 컬럼
  const adminColumns: TableColumnsType<AccountData> = useMemo(() => {
    if (!isAdmin()) return [];
    const allIds = filteredTableData.map((d) => d.id);
    const allChecked = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
    const indeterminate = allIds.some((id) => selectedIds.has(id)) && !allChecked;
    return [
      {
        title: (
          <Checkbox
            checked={allChecked}
            indeterminate={indeterminate}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedIds(new Set(allIds));
              } else {
                setSelectedIds(new Set());
              }
            }}
          />
        ),
        key: 'selection',
        width: 40,
        align: 'center' as const,
        render: (_: unknown, record: AccountData) => (
          <Checkbox
            checked={selectedIds.has(record.id)}
            onChange={(e) => {
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (e.target.checked) {
                  next.add(record.id);
                } else {
                  next.delete(record.id);
                }
                return next;
              });
            }}
          />
        ),
      },
    ];
  }, [isAdmin, filteredTableData, selectedIds]);

  const finalColumns = useMemo(() => [...adminColumns, ...columns], [adminColumns]);

  // 정산일 변경 핸들러
  const handleAccountDateChange = useCallback(async () => {
    if (selectedIds.size === 0) {
      message.warning('변경할 항목을 선택해주세요.');
      return;
    }
    if (!accountDate) {
      message.warning('정산일을 선택해주세요.');
      return;
    }
    setActionLoading(true);
    try {
      await accountShopOrderList(Array.from(selectedIds), accountDate.toDate());
      message.success(`${selectedIds.size}건의 정산일이 변경되었습니다.`);
      setSelectedIds(new Set());
      fetchDetailData();
    } catch (err) {
      console.error('정산일 변경 실패', err);
      message.error('정산일 변경에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  }, [selectedIds, accountDate, fetchDetailData]);

  // 상조정산일 변경 핸들러
  const handleRebateDateChange = useCallback(async () => {
    if (selectedIds.size === 0) {
      message.warning('변경할 항목을 선택해주세요.');
      return;
    }
    if (!rebateDate) {
      message.warning('상조정산일을 선택해주세요.');
      return;
    }
    setActionLoading(true);
    try {
      await rebateShopOrderList(Array.from(selectedIds), rebateDate.toDate());
      message.success(`${selectedIds.size}건의 상조정산일이 변경되었습니다.`);
      setSelectedIds(new Set());
      fetchDetailData();
    } catch (err) {
      console.error('상조정산일 변경 실패', err);
      message.error('상조정산일 변경에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  }, [selectedIds, rebateDate, fetchDetailData]);

  // 정산취소 핸들러
  const handleCancelAccount = useCallback(async () => {
    if (selectedIds.size === 0) {
      message.warning('취소할 항목을 선택해주세요.');
      return;
    }
    setActionLoading(true);
    try {
      await cancelAccountShopOrderList(Array.from(selectedIds));
      message.success(`${selectedIds.size}건의 정산이 취소되었습니다.`);
      setSelectedIds(new Set());
      fetchDetailData();
    } catch (err) {
      console.error('정산취소 실패', err);
      message.error('정산취소에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  }, [selectedIds, fetchDetailData]);

  // 상조정산취소 핸들러
  const handleCancelRebate = useCallback(async () => {
    if (selectedIds.size === 0) {
      message.warning('취소할 항목을 선택해주세요.');
      return;
    }
    setActionLoading(true);
    try {
      await cancelRebateShopOrderList(Array.from(selectedIds));
      message.success(`${selectedIds.size}건의 상조정산이 취소되었습니다.`);
      setSelectedIds(new Set());
      fetchDetailData();
    } catch (err) {
      console.error('상조정산취소 실패', err);
      message.error('상조정산취소에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  }, [selectedIds, fetchDetailData]);

  // 내부용
  const csvDataForSeller = tableData.map((item) => ({
    순번: item.index,
    접수일자: item.requestedAt
      ? formatDate(new Date(item.requestedAt), {
          dateSeparater: '/',
          contains: { year: false },
        })
      : '',
    발인일: item.coffinOutDate
      ? formatDate(new Date(item.coffinOutDate), {
          dateSeparater: '/',
          contains: { year: false },
        })
      : '',
    주소: item.address?.split('(')[0],
    장례식장: item.funeralHomeInfo,
    문구: item.senderPhrase,
    상품명: item.itemName,
    소비자가: (item.priceRetail ?? 0).toLocaleString(),
    화환가: (item.priceWhole ?? 0).toLocaleString(),
    상조회사: (item.priceRebateAgency ?? 0).toLocaleString(),
    지도사: (item.priceRebateWorker ?? 0).toLocaleString(),
    PG: item.pricePG,
    소속: item.teamName,
    지도사성함: item.workerName,
    휴대번호: item.workerPhoneNumber,
  }));

  // 제출용
  const csvDataForSubmit = tableData.map((item) => ({
    순번: item.index,
    접수일자: item.requestedAt
      ? formatDate(new Date(item.requestedAt), {
          dateSeparater: '/',
          contains: { year: false },
        })
      : '',
    발인일: item.coffinOutDate
      ? formatDate(new Date(item.coffinOutDate), {
          dateSeparater: '/',
          contains: { year: false },
        })
      : '',
    주소: item.address?.split('(')[0],
    장례식장: item.funeralHomeInfo,
    문구: item.senderPhrase,
    상품명: item.itemName,
    소비자가: (item.priceRetail ?? 0).toLocaleString(),
    화환가: ((item.priceWhole ?? 0) % 10000 === 7500
      ? (item.priceWhole ?? 0) + 2500
      : item.priceWhole ?? 0
    ).toLocaleString(),
    상조회사: (item.priceRebateAgency ?? 0).toLocaleString(),
    지도사: (item.priceRebateWorker ?? 0).toLocaleString(),
    PG: Math.round((item.priceRetail ?? 0) * 0.033),
    소속: item.teamName,
    지도사성함: item.workerName,
    휴대번호: item.workerPhoneNumber,
  }));

  return (
    <Modal openId={openId} className="max-w-[90%] sm:max-w-[90%]">
      <div className="space-y-4">
        <div className="flex items-center justify-between pt-2 pr-2">
          <h2 className="bugo-h1 text-20">
            상세 세부 내역 ({year}년 {month}월)
          </h2>
          <div className="center-box gap-2">
            <CSVLink
              filename={'Expense_Table.csv'}
              data={_.flatMapDeep([csvDataForSubmit])}
              className="center-box rounded-md px-4 py-2 font-bold theme-bg-main"
            >
              <p className="theme-text-4">엑셀 다운로드(제출용)</p>
            </CSVLink>
            <CSVLink
              filename={'Expense_Table.csv'}
              data={_.flatMapDeep([csvDataForSeller])}
              className="center-box rounded-md px-4 py-2 font-bold theme-bg-13"
            >
              <p className="theme-text-4">엑셀 다운로드(내부용)</p>
            </CSVLink>
          </div>
        </div>
        {/* 검색 */}
        <Input.Search
          placeholder="장례식장, 문구, 지도사명, 소속 검색"
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          size="small"
        />
        <div>
          <Table
            className="w-full"
            columns={finalColumns}
            dataSource={_.flatMapDeep([filteredTableData])}
            loading={loading}
            pagination={{
              position: ['bottomCenter'],
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: [10, 15, 20, 50, 100],
            }}
            rowKey={'id'}
            bordered
            size={'small'}
            summary={() => {
              // admin이면 체크박스 컬럼이 앞에 1개 추가됨
              const off = isAdmin() ? 1 : 0;
              return summary ? (
                <Table.Summary fixed>
                  <Table.Summary.Row className="bg-gray-50">
                    {/* admin 체크박스 열 빈칸 */}
                    {isAdmin() && (
                      <Table.Summary.Cell index={0} align="center">
                        {''}
                      </Table.Summary.Cell>
                    )}
                    {/* 순번 → 합계 */}
                    <Table.Summary.Cell index={0 + off} align="center">
                      <span className="font-bold">합계</span>
                    </Table.Summary.Cell>
                    {/* 결제일+발인일 → 해당 월 */}
                    <Table.Summary.Cell index={1 + off} colSpan={2} align="center">
                      <span className="font-bold">
                        {year}년 {month}월
                      </span>
                    </Table.Summary.Cell>
                    {/* 주소 → 총 건수 */}
                    <Table.Summary.Cell index={3 + off} align="center">
                      <span className="font-bold">총 건수: {summary.totalCount}</span>
                    </Table.Summary.Cell>
                    {/* 장례식장+문구 → 총 화환 */}
                    <Table.Summary.Cell index={4 + off} colSpan={2} align="center">
                      <span className="font-bold">
                        총 화환: {summary.totalPriceWhole.toLocaleString()}원
                      </span>
                    </Table.Summary.Cell>
                    {/* 상품명+소비자가 → 총 상조 */}
                    <Table.Summary.Cell index={6 + off} colSpan={3} align="center">
                      <span className="font-bold">
                        총 상조: {summary.totalPriceRebateAgency.toLocaleString()}원
                      </span>
                    </Table.Summary.Cell>
                    {/* 화환+상조 → 총 지도사 */}
                    <Table.Summary.Cell index={8 + off} colSpan={4} align="center">
                      <span className="font-bold">
                        총 지도사: {summary.totalPriceRebateWorker.toLocaleString()}원
                      </span>
                    </Table.Summary.Cell>
                    {/* PG → PG 합산 */}
                    <Table.Summary.Cell index={11 + off} colSpan={4} align="center">
                      <span className="font-bold">
                        PG: {summary.totalPricePG.toLocaleString()}원
                      </span>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null;
            }}
          />
          {/* ─── 하단: Admin 버튼(좌) + PG수수료/정산금(우) ─── */}
          <div className="mt-4 flex items-center justify-between gap-3">
            {/* 좌측: Admin 전용 정산일/상조정산일 변경 */}
            {isAdmin() ? (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <DatePicker
                    value={accountDate}
                    onChange={(date) => setAccountDate(date)}
                    size="small"
                    style={{ width: 120 }}
                  />
                  <button
                    className="whitespace-nowrap rounded bg-blue-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={selectedIds.size === 0 || actionLoading}
                    onClick={handleAccountDateChange}
                  >
                    정산일 변경
                  </button>
                  <button
                    className="whitespace-nowrap rounded bg-red-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={selectedIds.size === 0 || actionLoading}
                    onClick={handleCancelAccount}
                  >
                    정산취소
                  </button>
                </div>
                <div className="flex items-center gap-1.5">
                  <DatePicker
                    value={rebateDate}
                    onChange={(date) => setRebateDate(date)}
                    size="small"
                    style={{ width: 120 }}
                  />
                  <button
                    className="whitespace-nowrap rounded bg-green-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={selectedIds.size === 0 || actionLoading}
                    onClick={handleRebateDateChange}
                  >
                    상조정산일 변경
                  </button>
                  <button
                    className="whitespace-nowrap rounded bg-red-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={selectedIds.size === 0 || actionLoading}
                    onClick={handleCancelRebate}
                  >
                    상조정산취소
                  </button>
                </div>
                {selectedIds.size > 0 && (
                  <span className="text-xs text-gray-500">({selectedIds.size}건)</span>
                )}
              </div>
            ) : (
              <div />
            )}
            {/* 우측: PG수수료 + 정산금 */}
            <SettlementFooter
              pgFeeTotal={tableData.reduce((accu, prev) => accu + prev.pricePG, 0)}
              settlement={settlement}
              isAdmin={isAdmin()}
              totalPriceRetail={tableData.reduce(
                (accu, prev) => accu + prev.priceRetail,
                0,
              )}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
